
import * as React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { addContact } from '@/store/slices/contacts/contactsSlice';
import { useToast } from '@/hooks/use-toast';
import { v4 as uuidv4 } from 'uuid';
import { Contact } from '@/types/contact';
import { User, Mail, Building2, Star, X } from 'lucide-react';

interface CreateContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CreateContactDialog = ({ open, onOpenChange }: CreateContactDialogProps) => {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const [firstname, setFirstName] = React.useState('');
  const [lastname, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [isCustomer, setIsCustomer] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const newContact = {
        id: uuidv4(),
        firstname,
        lastname,
        email,
        type: isCustomer ? 'customer' : 'visitor' as const,
        status: 'active' as const,
        tags: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await dispatch(addContact(newContact as Partial<Contact>)).unwrap();
      
      toast({
        title: "Success",
        description: "Contact created successfully",
      });
      
      onOpenChange(false);
      setFirstName('');
      setLastName('');
      setEmail('');
      setIsCustomer(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create contact",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-white/95 backdrop-blur-sm border-slate-200/60 shadow-xl">
        {/* Enhanced Header */}
        <DialogHeader className="px-6 pt-6 pb-0 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <User className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                  Create New Contact
                </DialogTitle>
                <p className="text-sm text-slate-600 mt-0.5">Add a new contact to your workspace</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 rounded-full hover:bg-slate-100 transition-colors"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Form Content */}
        <div className="px-6 pb-6 pt-4">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Fields Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstname" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <User className="h-3.5 w-3.5 text-slate-500" />
                  First Name
                </Label>
                <Input
                  id="firstname"
                  value={firstname}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  required
                  className="h-11 bg-white/80 border-slate-200/60 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastname" className="text-sm font-medium text-slate-700">
                  Last Name
                </Label>
                <Input
                  id="lastname"
                  value={lastname}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  required
                  className="h-11 bg-white/80 border-slate-200/60 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-slate-500" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john.doe@example.com"
                required
                className="h-11 bg-white/80 border-slate-200/60 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
              />
            </div>

            {/* Customer Status */}
            <div className="bg-gradient-to-r from-blue-50/50 via-indigo-50/30 to-purple-50/50 rounded-xl p-4 border border-blue-200/40">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="isCustomer"
                  checked={isCustomer}
                  onCheckedChange={(checked) => setIsCustomer(checked as boolean)}
                  className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-blue-600" />
                  <Label htmlFor="isCustomer" className="text-sm font-medium text-slate-700 cursor-pointer">
                    Mark as customer
                  </Label>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-2 ml-6">
                Customers receive priority support and enhanced features
              </p>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200/60">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className="h-11 px-6 bg-white/80 border-slate-200/60 hover:bg-slate-50/80 transition-all duration-200"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="h-11 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25 transition-all duration-200 hover:shadow-blue-500/30 border-0"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Create Contact
                  </div>
                )}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateContactDialog;
